import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PaletteMode } from '@mui/material';

export type ThemeMode = PaletteMode | 'system';

interface PreferencesState {
  mode: ThemeMode;
  sidebarCollapsed: boolean;
  density: 'compact' | 'standard' | 'comfortable';
  setMode: (mode: ThemeMode) => void;
  toggleSidebar: () => void;
  setDensity: (density: PreferencesState['density']) => void;
  /** `system` resolved against the OS setting, so the theme never sees 'system'. */
  resolvedMode: () => PaletteMode;
}

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      // Light by default (owner's call, 2026-09-17); `system` is a choice, not the start.
      mode: 'light',
      sidebarCollapsed: false,
      density: 'standard',
      setMode: (mode) => set({ mode }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setDensity: (density) => set({ density }),
      resolvedMode: () => {
        const { mode } = get();
        if (mode !== 'system') return mode;
        return prefersDark() ? 'dark' : 'light';
      },
    }),
    { name: 'uruanna.preferences', partialize: (s) => ({ mode: s.mode, sidebarCollapsed: s.sidebarCollapsed, density: s.density }) },
  ),
);
